/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from "@solana/kit";

export const EXTERNAL_ID_MAPPING_DISCRIMINATOR = new Uint8Array([
  180, 218, 64, 128, 181, 84, 116, 180,
]);

export function getExternalIdMappingDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    EXTERNAL_ID_MAPPING_DISCRIMINATOR,
  );
}

export type ExternalIdMapping = {
  discriminator: ReadonlyUint8Array;
  /** The Ghost (Agent) PDA this maps to */
  ghostPubkey: Address;
  /** Platform name ("payai", "eliza", "crossmint", etc.) */
  platform: string;
  /** Platform-specific agent ID */
  externalId: string;
  /** When this mapping was created */
  createdAt: bigint;
  /** Whether ownership has been verified */
  verified: boolean;
  /** When ownership was verified (if applicable) */
  verifiedAt: Option<bigint>;
  /** PDA bump seed */
  bump: number;
};

export type ExternalIdMappingArgs = {
  /** The Ghost (Agent) PDA this maps to */
  ghostPubkey: Address;
  /** Platform name ("payai", "eliza", "crossmint", etc.) */
  platform: string;
  /** Platform-specific agent ID */
  externalId: string;
  /** When this mapping was created */
  createdAt: number | bigint;
  /** Whether ownership has been verified */
  verified: boolean;
  /** When ownership was verified (if applicable) */
  verifiedAt: OptionOrNullable<number | bigint>;
  /** PDA bump seed */
  bump: number;
};

/** Gets the encoder for {@link ExternalIdMappingArgs} account data. */
export function getExternalIdMappingEncoder(): Encoder<ExternalIdMappingArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["ghostPubkey", getAddressEncoder()],
      ["platform", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["externalId", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["createdAt", getI64Encoder()],
      ["verified", getBooleanEncoder()],
      ["verifiedAt", getOptionEncoder(getI64Encoder())],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({ ...value, discriminator: EXTERNAL_ID_MAPPING_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link ExternalIdMapping} account data. */
export function getExternalIdMappingDecoder(): Decoder<ExternalIdMapping> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["ghostPubkey", getAddressDecoder()],
    ["platform", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["externalId", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["createdAt", getI64Decoder()],
    ["verified", getBooleanDecoder()],
    ["verifiedAt", getOptionDecoder(getI64Decoder())],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link ExternalIdMapping} account data. */
export function getExternalIdMappingCodec(): Codec<
  ExternalIdMappingArgs,
  ExternalIdMapping
> {
  return combineCodec(
    getExternalIdMappingEncoder(),
    getExternalIdMappingDecoder(),
  );
}

export function decodeExternalIdMapping<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<ExternalIdMapping, TAddress>;
export function decodeExternalIdMapping<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<ExternalIdMapping, TAddress>;
export function decodeExternalIdMapping<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
):
  | Account<ExternalIdMapping, TAddress>
  | MaybeAccount<ExternalIdMapping, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getExternalIdMappingDecoder(),
  );
}

export async function fetchExternalIdMapping<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<ExternalIdMapping, TAddress>> {
  const maybeAccount = await fetchMaybeExternalIdMapping(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeExternalIdMapping<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<ExternalIdMapping, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeExternalIdMapping(maybeAccount);
}

export async function fetchAllExternalIdMapping(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<ExternalIdMapping>[]> {
  const maybeAccounts = await fetchAllMaybeExternalIdMapping(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeExternalIdMapping(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<ExternalIdMapping>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeExternalIdMapping(maybeAccount),
  );
}
